// features/workstations/components/morning-brief-card.js
// ═══════════════════════════════════════════════════════════
// "Today's Brief" card on the Workstation Home page.
//
// Pulls the latest briefings row (cron-generated daily) and
// shows the synthesis line collapsed by default, with the
// bullets revealed on click.
//
// Domain prop drives which brief to fetch — "trading" today,
// "investing" / etc. as future workstations come online. The
// briefings table is keyed by domain, not workstation, since
// the underlying market data feeds the same brief for every
// trading workstation.
// ═══════════════════════════════════════════════════════════

import { useEffect, useState } from 'react';

export default function MorningBriefCard({ domain = 'trading' }) {
  const [briefing, setBriefing] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expanded, setExpanded] = useState(false);
  // Commit 71 — manual regenerate state
  const [regenerating, setRegenerating] = useState(false);
  const [regenError, setRegenError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const res = await fetch(`/api/workstations/briefing?domain=${encodeURIComponent(domain)}`, {
          cache: 'no-store',
        });
        if (cancelled) return;
        const data = res.ok ? await res.json() : { briefing: null };
        setBriefing(data.briefing || null);
      } catch (e) {
        console.warn('[morning-brief] load failed:', e?.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [domain]);

  // Commit 71 — manual regenerate
  async function handleRegenerate() {
    if (regenerating) return;
    setRegenerating(true);
    setRegenError(null);
    try {
      const res = await fetch('/api/workstations/briefing', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ domain }),
      });
      const data = await res.json();
      if (!res.ok) {
        setRegenError(data?.error || `HTTP ${res.status}`);
        return;
      }
      setBriefing(data.briefing || null);
    } catch (e) {
      setRegenError(e?.message || 'regenerate failed');
    } finally {
      setRegenerating(false);
    }
  }

  if (loading) return null;            // silently absent while loading
  if (!briefing?.synthesis) return null; // no brief yet → don't render

  const bullets = Array.isArray(briefing.bullets)
    ? briefing.bullets
    : (typeof briefing.bullets === 'string' ? safeParseBullets(briefing.bullets) : []);

  const dateLabel = formatBriefDate(briefing.briefing_date);
  const generated = relTime(briefing.generated_at);
  // Commit 71 — staleness flag (>6h is "stale" worth flagging)
  const ageMs = briefing.generated_at ? (Date.now() - new Date(briefing.generated_at).getTime()) : 0;
  const isStale = ageMs > 6 * 60 * 60 * 1000;

  return (
    <div className="ws-brief-card">
      <button
        type="button"
        className="ws-brief-header"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
      >
        <div className="ws-brief-header-left">
          <span className="ws-brief-eyebrow">Today&rsquo;s Brief</span>
          <span className="ws-brief-meta">
            {dateLabel} · {domain} · {generated}
          </span>
        </div>
        <span className={`ws-brief-chev${expanded ? ' open' : ''}`}>▾</span>
      </button>

      <div className="ws-brief-synthesis">{briefing.synthesis}</div>

      {expanded && bullets.length > 0 && (
        <div className="ws-brief-bullets">
          {bullets.map((b, i) => {
            const text = typeof b === 'string' ? b : (b.text || b.body || JSON.stringify(b));
            return (
              <div key={i} className="ws-brief-bullet">
                <span className="ws-brief-bullet-dot">•</span>
                <span className="ws-brief-bullet-text">{text}</span>
              </div>
            );
          })}
        </div>
      )}

      {/* Commit 71 — Footer with manual regenerate + freshness.
          Always visible (not gated on expanded) so the user can
          refresh from the collapsed state. */}
      <div className="ws-brief-footer-row">
        <div className="ws-brief-footer-meta">
          <span>
            briefing · <strong>{briefing.briefing_date}</strong>
            {generated && <> · {generated}</>}
            {isStale && <span className="ws-brief-stale-flag"> · STALE</span>}
          </span>
          {expanded && briefing.source_count != null && (
            <span style={{ marginLeft: 12 }}>
              {briefing.source_count} sources · {briefing.model || 'sonnet'}
            </span>
          )}
        </div>
        <button
          type="button"
          className="ws-brief-regen-btn"
          onClick={(e) => { e.stopPropagation(); handleRegenerate(); }}
          disabled={regenerating}
          title="Regenerate using the latest market bundle"
        >
          {regenerating ? 'Regenerating…' : '↻ Regenerate'}
        </button>
      </div>
      {regenError && (
        <div className="ws-brief-regen-error">{regenError}</div>
      )}
    </div>
  );
}

function safeParseBullets(s) {
  try {
    const parsed = JSON.parse(s);
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function formatBriefDate(d) {
  if (!d) return '';
  try {
    const dt = new Date(d + 'T12:00:00Z');
    const today = new Date();
    if (dt.toDateString() === today.toDateString()) return 'Today';
    const y = new Date(); y.setDate(y.getDate() - 1);
    if (dt.toDateString() === y.toDateString()) return 'Yesterday';
    return dt.toLocaleDateString('en-US', { weekday: 'short', month: 'short', day: 'numeric' });
  } catch { return d; }
}

function relTime(iso) {
  if (!iso) return '';
  const then = new Date(iso).getTime();
  if (Number.isNaN(then)) return '';
  const diff = Date.now() - then;
  const min = Math.floor(diff / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const d = Math.floor(hr / 24);
  return `${d}d ago`;
}
